'use client';

import { useId } from 'react';

import {
  HelpCircle,
  Lightbulb,
  MessageCircleWarning,
  type LucideIcon,
} from 'lucide-react';

import type { getPostHogSupportCopy } from '@/i18n/posthog-support-copy';
import { cn } from '@/lib/utils';

import { PostHogSupportAskImai } from './posthog-support-ask-imai';

import {
  getPostHogSupportContentWidth,
  getPostHogSupportHorizontalPadding,
} from './posthog-support-layout';
import {
  PostHogSupportOptionContent,
  postHogSupportOptionClassName,
} from './posthog-support-option';
import type { SupportIntent } from './posthog-support-state';

type SupportCopy = ReturnType<typeof getPostHogSupportCopy>;

type IntentOption = {
  description: string;
  icon: LucideIcon;
  intent: SupportIntent;
  label: string;
};

function getIntentOptions(copy: SupportCopy): IntentOption[] {
  return [
    {
      intent: 'problem',
      label: copy.reportProblem,
      description: copy.reportProblemDescription,
      icon: MessageCircleWarning,
    },
    {
      intent: 'question',
      label: copy.askTeam,
      description: copy.askTeamDescription,
      icon: HelpCircle,
    },
    {
      intent: 'idea',
      label: copy.shareIdea,
      description: copy.shareIdeaDescription,
      icon: Lightbulb,
    },
  ];
}

/**
 * Ask IMAI comes first: an answer now, in the member's Your IMAI chat. The
 * three request types below go to the team.
 */
export function PostHogSupportIntentChooser({
  compact,
  copy,
  dialog = false,
  onChoose,
}: {
  compact: boolean;
  copy: SupportCopy;
  dialog?: boolean;
  onChoose: (intent: SupportIntent) => void;
}) {
  const descriptionIdPrefix = useId();
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'min-h-0 flex-1 overflow-y-auto',
        getPostHogSupportHorizontalPadding(compact),
        compact ? 'py-3' : 'py-6 sm:py-10'
      )}
    >
      <div className={getPostHogSupportContentWidth(compact)}>
        <h2
          id={headingId}
          className="sr-only"
          data-support-view-heading
          tabIndex={-1}
        >
          {copy.newRequest}
        </h2>
        <div
          role="group"
          aria-labelledby={headingId}
          className={cn(
            compact
              ? 'divide-border/35 border-border/35 divide-y overflow-hidden rounded-xl border'
              : 'grid gap-3 md:grid-cols-3'
          )}
        >
          <PostHogSupportAskImai
            compact={compact}
            copy={copy}
            dialog={dialog}
            // One full-width row above the three request cards.
            className="md:col-span-3"
          />
          {getIntentOptions(copy).map((option) => {
            const descriptionId = `${descriptionIdPrefix}-${option.intent}`;
            return (
              <button
                key={option.intent}
                type="button"
                aria-describedby={descriptionId}
                className={postHogSupportOptionClassName(compact)}
                onClick={() => onChoose(option.intent)}
              >
                <PostHogSupportOptionContent
                  compact={compact}
                  description={option.description}
                  descriptionId={descriptionId}
                  icon={option.icon}
                  label={option.label}
                />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
